import { useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, ChevronLeft, CheckCircle, Target } from 'lucide-react';

/**
 * Petit test rapide (100% local / hors ligne).
 * Questions et réponses figées — aucun appel réseau, aucun token requis.
 * À la fin, l'élève est toujours dirigé vers le Niveau 1 pour suivre tous les modules dans l'ordre.
 * Difficulté croissante : Q1→5 faciles, Q6→7 plus difficiles, Q8→10 très difficiles.
 */
const QUESTIONS = [
  { id: 'q1', question: 'Choose the correct: I ___ a student.',
    options: ['am', 'is', 'are', 'be'], answer: 'am' },
  { id: 'q2', question: "What is the past of 'go' ?",
    options: ['goed', 'went', 'gone', 'going'], answer: 'went' },
  { id: 'q3', question: 'She ___ to the gym every Monday.',
    options: ['go', 'goes', 'going', 'is goes'], answer: 'goes' },
  { id: 'q4', question: 'How ___ coffee do you drink?',
    options: ['many', 'much', 'more', 'lot'], answer: 'much' },
  { id: 'q5', question: 'I have lived here ___ 2015.',
    options: ['since', 'for', 'from', 'during'], answer: 'since' },
  { id: 'q6', question: 'If I ___ rich, I would travel the world.',
    options: ['am', 'was', 'were', 'be'], answer: 'were' },
  { id: 'q7', question: "Choose the synonym of 'reluctant':",
    options: ['eager', 'hesitant', 'honest', 'happy'], answer: 'hesitant' },
  { id: 'q8', question: 'By next year, I ___ here for ten years.',
    options: ['will work', 'will have worked', 'have worked', 'am working'], answer: 'will have worked' },
  { id: 'q9', question: 'The proposal was ___ by the board with little discussion.',
    options: ['turned over', 'rolled out', 'rubber-stamped', 'knocked down'], answer: 'rubber-stamped' },
  { id: 'q10', question: 'Which sentence is grammatically idiomatic?',
    options: [
      'Were it not for her dedication, the project would have failed.',
      'If she would not have helped, the project will fail.',
      "Hadn't she help, the project would fail.",
      "If she didn't helped, the project failed.",
    ], answer: 'Were it not for her dedication, the project would have failed.' },
];

export default function LevelTest({ onResult, onSkip, onBack }) {
  const [idx, setIdx]         = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult]   = useState(null);

  const choose = (qid, opt) => setAnswers({ ...answers, [qid]: opt });
  const current = QUESTIONS[idx];

  const submit = () => {
    let score = 0;
    QUESTIONS.forEach((q) => { if (answers[q.id] === q.answer) score += 1; });
    setResult({ score, total: QUESTIONS.length });
  };

  if (result) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-xl mx-auto px-4 py-12" data-testid="level-test-result">
        <div className="bg-gradient-to-br from-emerald-900/40 to-slate-900 border border-emerald-500/30 rounded-3xl p-8 text-center">
          <div className="text-6xl mb-3">🎉</div>
          <div className="inline-flex items-center gap-2 bg-yellow-400 text-slate-950 font-bold px-3 py-1 rounded-full mb-3">
            <Target size={14} />Petit test terminé
          </div>
          <h3 className="text-2xl font-bold">Score : {result.score}/{result.total}</h3>
          <p className="text-sm text-white/70 mt-4 leading-relaxed">
            Bravo ! Pour profiter de tous les modules et construire des bases solides,
            on te recommande de commencer par le <span className="text-emerald-300 font-semibold">Niveau 1</span>.
            Tu avanceras vite si certaines notions te sont déjà familières.
          </p>
          <button onClick={() => onResult(result)} data-testid="level-test-start"
            className="mt-6 w-full bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold py-3 rounded-xl">
            Commencer au Niveau 1 →
          </button>
        </div>

        {/* Révision : toutes les questions et les bonnes réponses */}
        <div className="mt-6 text-left" data-testid="level-test-review">
          <h4 className="font-bold text-lg mb-1">Corrigé du test</h4>
          <p className="text-sm text-white/60 mb-4">Revois chaque question et la bonne réponse avant de commencer.</p>
          <div className="space-y-3">
            {QUESTIONS.map((q, i) => {
              const mine = answers[q.id];
              const ok = mine === q.answer;
              return (
                <div key={q.id} className="bg-white/5 border border-white/10 rounded-xl p-4" data-testid={`review-${q.id}`}>
                  <div className="text-sm font-semibold mb-2">{i + 1}. {q.question}</div>
                  <div className="text-sm text-emerald-300">✓ Bonne réponse : <strong>{q.answer}</strong></div>
                  {!ok && (
                    <div className="text-sm text-amber-300 mt-1">Ta réponse : {mine || '—'}</div>
                  )}
                </div>
              );
            })}
          </div>
          <button onClick={() => onResult(result)} data-testid="level-test-start-bottom"
            className="mt-5 w-full bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold py-3 rounded-xl">
            Commencer au Niveau 1 →
          </button>
        </div>
      </motion.div>
    );
  }

  const all = Object.keys(answers).length === QUESTIONS.length;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-xl mx-auto px-4 py-10" data-testid="level-test">
      <button onClick={onBack} className="text-white/60 hover:text-white text-sm mb-4 flex items-center gap-1"><ChevronLeft size={16} />Retour</button>
      <div className="bg-gradient-to-br from-emerald-900/30 to-slate-900 border border-emerald-500/30 rounded-3xl p-6">
        <div className="inline-flex items-center gap-2 bg-emerald-500/15 text-emerald-300 px-3 py-1 rounded-full text-xs font-bold mb-3">
          <Target size={12} />Petit test rapide
        </div>
        <h2 className="text-2xl font-bold">Fais un petit test</h2>
        <p className="text-sm text-white/60 mt-1">10 questions — environ 3 minutes. Juste pour t'échauffer avant de démarrer.</p>
        <div className="flex gap-1 mt-4">
          {QUESTIONS.map((_, i) => <div key={i} className={`h-1.5 flex-1 rounded-full ${answers[QUESTIONS[i].id] ? 'bg-emerald-400' : i === idx ? 'bg-yellow-400' : 'bg-white/10'}`} />)}
        </div>
      </div>

      <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mt-4" data-testid={`question-${current.id}`}>
        <div className="text-xs text-emerald-300 mb-2">Question {idx + 1} / {QUESTIONS.length}</div>
        <div className="text-lg font-bold mb-4">{current.question}</div>
        <div className="space-y-2">
          {current.options.map((opt, i) => (
            <button key={i} onClick={() => choose(current.id, opt)} data-testid={`opt-${i}`}
              className={`w-full text-left p-3 rounded-lg border transition ${answers[current.id] === opt ? 'border-emerald-400 bg-emerald-500/20' : 'border-white/10 bg-white/5 hover:border-white/30'}`}>
              <span className="text-sm">{opt}</span>
              {answers[current.id] === opt && <CheckCircle size={14} className="inline ml-2 text-emerald-400" />}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-2 mt-4">
        <button disabled={idx === 0} onClick={() => setIdx(idx - 1)}
          className="px-4 py-2.5 bg-white/5 hover:bg-white/10 disabled:opacity-30 rounded-lg text-sm">Précédent</button>
        {idx < QUESTIONS.length - 1 ? (
          <button disabled={!answers[current.id]} onClick={() => setIdx(idx + 1)} data-testid="next-question"
            className="flex-1 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-slate-950 font-bold py-2.5 rounded-lg">Suivant</button>
        ) : (
          <button disabled={!all} onClick={submit} data-testid="submit-test"
            className="flex-1 bg-yellow-500 hover:bg-yellow-600 disabled:opacity-40 text-slate-950 font-bold py-2.5 rounded-lg flex items-center justify-center gap-2">
            <Sparkles size={14} />Voir mon résultat
          </button>
        )}
      </div>

      <button onClick={onSkip} className="mt-3 w-full text-xs text-white/50 hover:text-white" data-testid="skip-test">
        Passer le test et commencer du début
      </button>
    </motion.div>
  );
}
